import { useMemo, useState } from 'react';
import { punchApi, type PunchListResponse } from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { carryOverGroups, STAGE_LABELS } from './punchModel';

/**
 * E&W Internal Punch → Published Punch List (PM/PE), in three steps: which
 * subs' open items carry over, the architect/owner walk, and what changes.
 * Carried items keep their numbers; the rest close as "not carried over" and
 * stay in history. One-way: the list can't go back to internal.
 */
export default function PunchPublishModal({ projectId, data, onClose, onPublished }: {
  projectId: number;
  data: PunchListResponse;
  onClose: () => void;
  onPublished: () => void;
}) {
  const groups = useMemo(() => carryOverGroups(data.items), [data.items]);
  const [step, setStep] = useState(0);
  // What the PM UN-ticked, not what is ticked: the list can refetch while the
  // wizard is open (window focus), and a group that appears then must default
  // to carrying over — never to closing items nobody chose to close.
  const [dropped, setDropped] = useState<Set<string>>(() => new Set());
  const carry = useMemo(() => new Set(groups.map((g) => g.key).filter((k) => !dropped.has(k))), [groups, dropped]);
  const [walkDate, setWalkDate] = useState('');
  const [walkers, setWalkers] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const carried = groups.filter((g) => carry.has(g.key)).reduce((n, g) => n + g.inPlay, 0);
  const closing = groups.filter((g) => !carry.has(g.key)).reduce((n, g) => n + g.inPlay, 0);
  const alreadyClosed = data.items.filter((i) => i.status === 'closed').length;
  const ao = data.list!.requiresAoApproval;
  const toggle = (key: string) => setDropped((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      // The groups to CLOSE, not the ones to carry: an assignee whose items
      // were added while this dialog was open is in neither list here, and
      // the server then carries it over rather than closing it unchosen
      // (Codex on #565).
      await punchApi.publish(projectId, {
        closeGroups: groups.filter((g) => !carry.has(g.key)).map((g) => g.target),
        walkDate: walkDate || undefined,
        walkAttendees: walkers.trim() || undefined,
      });
      onPublished();
      onClose();
    } catch (e) {
      // Publishing may have gone through with only the reply lost; a retry
      // would then be refused as "already published". Check before saying
      // it failed (Codex on #565).
      const now = await punchApi.get(projectId).catch(() => null);
      if (now?.list && now.list.stage !== 'internal') {
        onPublished();
        onClose();
        return;
      }
      setError((e as Error)?.message ?? 'The list was not published.');
    } finally {
      setBusy(false);
    }
  };

  const steps = ['Carry over', 'Walk details', 'Confirm'];
  return (
    <Modal
      isOpen onClose={onClose} title="Publish the punch list" maxWidth={720} closeOnBackdropClick={!busy}
      footer={(
        <>
          {step > 0 && <Button variant="secondary" onClick={() => setStep(step - 1)} disabled={busy}>Back</Button>}
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          {step < 2
            ? <Button variant="primary" onClick={() => setStep(step + 1)}>Next</Button>
            : <Button variant="primary" onClick={publish} isLoading={busy}>Publish List</Button>}
        </>
      )}
    >
      <div className="pl-form">
        <div className="pl-wizard-steps">
          {steps.map((s, i) => <span key={s}>{i > 0 ? '→ ' : ''}{i === step ? <b>{s}</b> : s}</span>)}
        </div>

        {step === 0 && (
          <>
            <p className="pl-muted" style={{ margin: 0 }}>
              Open items (New, Open, Rejected, Ready for review) of the subs you tick carry over and keep their numbers.
              The others close as “not carried over”. Verified and closed items stay as they are.
            </p>
            {groups.length === 0 && <p className="pl-muted">No open items — everything carries as it is.</p>}
            {groups.length > 0 && (
              <div className="pl-row">
                <button type="button" className="pl-link" onClick={() => setDropped(new Set())}>Select all</button>
                <button type="button" className="pl-link" onClick={() => setDropped(new Set(groups.map((g) => g.key)))}>Select none</button>
                <span className="pl-spacer" />
                <span className="pl-muted">{carried} carry over · {closing} close</span>
              </div>
            )}
            <div>
              {groups.map((g) => (
                <label key={g.key} className="pl-carry">
                  <input type="checkbox" checked={carry.has(g.key)} onChange={() => toggle(g.key)} />
                  <span>{g.label}</span>
                  <span className="pl-muted">{carry.has(g.key) ? 'Carry over' : 'Close — not carried'}</span>
                  <span className="ew-num">{g.inPlay} open</span>
                </label>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <div className="pl-grid2">
            <label className="pl-field"><span>Walk date</span>
              <input type="date" value={walkDate} onChange={(e) => setWalkDate(e.target.value)} />
            </label>
            <label className="pl-field"><span>Walked by</span>
              <input value={walkers} maxLength={500} placeholder="Architect, owner rep, our PM…"
                onChange={(e) => setWalkers(e.target.value)} />
            </label>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="pl-caption">Here’s what changes when you publish</div>
            <div className="pl-changes">
              <span className="pl-muted">List stage</span><span>{STAGE_LABELS.internal} → {STAGE_LABELS.published}</span>
              <span className="pl-muted">Carry over</span><span>{carried} item{carried === 1 ? '' : 's'} keep their numbers</span>
              <span className="pl-muted">Not carried</span><span>{closing} item{closing === 1 ? '' : 's'} close as “not carried over” and stay in history</span>
              <span className="pl-muted">Already closed</span><span>{alreadyClosed} stay in history</span>
              <span className="pl-muted">New items</span><span>Added from now on are tagged Published</span>
              <span className="pl-muted">Architect/owner approval</span><span>{ao ? 'On' : 'Off'}</span>
            </div>
            <div className="pl-warn">You can’t move the list back to {STAGE_LABELS.internal} after publishing.</div>
          </>
        )}

        {error && <div className="pl-error">{error}</div>}
      </div>
    </Modal>
  );
}
